/**
 * INSPECT popover + universal queue wiring for the merged table's
 * availability grid (offer-unified-table, CU-86cbc3r8b): the reserve/
 * upgrade/close-sale/cancel/force-release action gates, the queue-join
 * panel for the currently-open cell, and the popover's render callback.
 * Extracted from `use-offer-table-availability.tsx` — see that file's own
 * doc comment.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { UnitAvailabilityRow } from '@ooh/contracts';
import {
  buildQueueJoinPanelLabels,
  buildQueueJoinSection,
  InspectPopover,
  useCellQueueJoin,
  useQueueClientOptions,
  type InventoryAvailabilityPeriod,
  useQueueOfferOptions,
} from '@ooh/screens';
import { useAvailabilityReservationActions } from '../../availability-shared/availability-reservation-actions';
import {
  findOfferUnitEntries,
  getOfferUnitActionableCell,
} from '../lib/offer-availability-mapping';

/** The four transition-permission gates `InspectPopover` renders quick actions for. */
interface InspectActionGates {
  canUpgradeHard: boolean;
  canCloseSale: boolean;
  canCancel: boolean;
  canForceRelease: boolean;
}

function buildInspectGates(
  canEdit: boolean,
  reservationActions: ReturnType<typeof useAvailabilityReservationActions>,
): InspectActionGates {
  return {
    canUpgradeHard: canEdit && reservationActions.canUpgradeHardReservation,
    canCloseSale: canEdit && reservationActions.canCloseSaleReservation,
    canCancel: canEdit && reservationActions.canCancelReservation,
    canForceRelease: canEdit && reservationActions.canForceReleaseReservation,
  };
}

/** Resolves each gate's quick-action handler, or `undefined` when withheld — kept separate from
 * `buildInspectGates` so neither function's ternary count trips `renderInspectContent`'s own complexity budget. */
function buildInspectHandlers(
  gates: InspectActionGates,
  reservationActions: ReturnType<typeof useAvailabilityReservationActions>,
  sel: { unitId: string; periodIds: string[] },
) {
  return {
    onUpgradeHard: gates.canUpgradeHard
      ? () => reservationActions.handleUpgradeHard(sel)
      : undefined,
    onCloseSale: gates.canCloseSale ? () => reservationActions.handleCloseSale(sel) : undefined,
    onCancel: gates.canCancel ? () => reservationActions.handleRelease(sel, false) : undefined,
    onForceRelease: gates.canForceRelease
      ? () => reservationActions.handleRelease(sel, true)
      : undefined,
  };
}

export interface UseOfferTableInspectQueueOptions {
  offerId: number;
  offerLabel: string;
  canEdit: boolean;
  periods: InventoryAvailabilityPeriod[];
  units: UnitAvailabilityRow[];
  mediaUnitIdById: Map<string, number>;
  refetchAvailability: () => void;
}

export interface UseOfferTableInspectQueueResult {
  openInspectCellKey: string | null;
  setOpenInspectCellKey: React.Dispatch<React.SetStateAction<string | null>>;
  /** `canEdit && reservationActions.canCreateReservation` — feeds the multi-select `canSelect` gate. */
  canCreateReservation: boolean;
  renderInspectContent: (params: {
    rowId: string;
    period: InventoryAvailabilityPeriod;
    state: string;
  }) => React.ReactNode;
}

export function useOfferTableInspectQueue({
  offerId,
  offerLabel,
  canEdit,
  periods,
  units,
  mediaUnitIdById,
  refetchAvailability,
}: UseOfferTableInspectQueueOptions): UseOfferTableInspectQueueResult {
  const { t: tReservations } = useTranslation('reservations');
  const [openInspectCellKey, setOpenInspectCellKey] = React.useState<string | null>(null);

  const getCellForActions = React.useCallback(
    (unitId: string, periodId: string) => {
      const period = periods.find((p) => p.id === periodId);
      if (!period) return null;
      return getOfferUnitActionableCell(units, unitId, period);
    },
    [periods, units],
  );

  const reservationActions = useAvailabilityReservationActions({
    offerId,
    canEdit,
    periods,
    units,
    getCell: getCellForActions,
    onActionComplete: () => {
      setOpenInspectCellKey(null);
      refetchAvailability();
    },
  });

  // ─── Universal queue popup for the currently-open INSPECT cell ───────────
  const openInspectQueueContext = React.useMemo(() => {
    const inactive = {
      mediaUnitId: 0,
      dateFrom: '',
      dateTo: '',
      enabled: false as const,
      ownership: 'other' as const,
    };
    if (!openInspectCellKey) return inactive;
    const separatorIndex = openInspectCellKey.indexOf(':');
    const rowId = openInspectCellKey.slice(0, separatorIndex);
    const periodId = openInspectCellKey.slice(separatorIndex + 1);
    const period = periods.find((p) => p.id === periodId);
    const mediaUnitId = mediaUnitIdById.get(rowId);
    if (!period || mediaUnitId === undefined) return inactive;
    const entries = findOfferUnitEntries(units, rowId, period.start, period.end);
    const dominant = entries[0];
    if (!dominant) return inactive;
    const isActiveHold =
      dominant.status === 'SOFT_RESERVATION' ||
      dominant.status === 'HARD_RESERVATION' ||
      dominant.status === 'OPTION';
    if (!isActiveHold) return inactive;
    return {
      mediaUnitId,
      dateFrom: period.start,
      dateTo: period.end,
      enabled: true as const,
      ownership: dominant.ownership === 'mine' ? ('mine' as const) : ('other' as const),
    };
  }, [openInspectCellKey, periods, mediaUnitIdById, units]);

  const {
    activeQueueState: openInspectQueueState,
    queueInfo: openInspectQueueInfo,
    currentUserId,
    handleJoin: handleJoinQueue,
    handleQueueAction,
    isJoinPending,
  } = useCellQueueJoin(openInspectQueueContext);
  const queueClientOptions = useQueueClientOptions();
  const queueOfferOptions = useQueueOfferOptions();
  const queueJoinPanelLabels = React.useMemo(
    () =>
      buildQueueJoinPanelLabels(
        tReservations as (key: string, options?: Record<string, unknown>) => string,
      ),
    [tReservations],
  );

  const renderInspectContent = React.useCallback(
    ({
      rowId,
      period,
      state,
    }: {
      rowId: string;
      period: InventoryAvailabilityPeriod;
      state: string;
    }) => {
      if (
        state === 'free' ||
        state === 'option-own' ||
        state === 'reserved-own-soft' ||
        state === 'reserved-own-hard'
      )
        return null;
      const entries = findOfferUnitEntries(units, rowId, period.start, period.end);
      const cellTestId = `offer-table-inspect-${rowId}-${period.id}`;
      const cellKey = `${rowId}:${period.id}`;
      const isOpen = openInspectCellKey === cellKey;
      const sel = { unitId: rowId, periodIds: [period.id] };
      const gates = buildInspectGates(canEdit, reservationActions);
      const handlers = buildInspectHandlers(gates, reservationActions, sel);
      const queueSection = buildQueueJoinSection(isOpen, cellTestId, {
        activeQueueState: openInspectQueueState,
        currentUserId,
        queueListId: offerId,
        clientOptions: queueClientOptions,
        offerOptions: queueOfferOptions,
        labels: queueJoinPanelLabels,
        onJoin: handleJoinQueue,
        onQueueAction: handleQueueAction,
        isJoinPending,
        offerIdentity: { offerId, label: offerLabel },
      });
      return (
        <InspectPopover
          entries={entries}
          cellState={state as never}
          unitLabel={rowId}
          dateRangeLabel={period.label}
          mediaUnitId={mediaUnitIdById.get(rowId) ?? 0}
          inspectedCellDateFrom={period.start}
          inspectedCellDateTo={period.end}
          hasLabelAuthorization={true}
          canUpgradeHard={gates.canUpgradeHard}
          canCloseSale={gates.canCloseSale}
          canCancel={gates.canCancel}
          canForceRelease={gates.canForceRelease}
          onUpgradeHard={handlers.onUpgradeHard}
          onCloseSale={handlers.onCloseSale}
          onCancel={handlers.onCancel}
          onForceRelease={handlers.onForceRelease}
          onExpired={refetchAvailability}
          queueInfo={isOpen ? openInspectQueueInfo : undefined}
          queueSection={queueSection}
          canManage={false}
          enquireLabel=""
          enquireHref=""
          setStatusManuallyLabel=""
          testId={cellTestId}
        />
      );
    },
    [
      units,
      canEdit,
      currentUserId,
      handleJoinQueue,
      handleQueueAction,
      isJoinPending,
      mediaUnitIdById,
      offerId,
      offerLabel,
      openInspectCellKey,
      openInspectQueueInfo,
      openInspectQueueState,
      queueClientOptions,
      queueJoinPanelLabels,
      queueOfferOptions,
      reservationActions,
      refetchAvailability,
    ],
  );

  return {
    openInspectCellKey,
    setOpenInspectCellKey,
    canCreateReservation: reservationActions.canCreateReservation,
    renderInspectContent,
  };
}
